<template>
  <div class="status-stepper">
    <span class="stepper-label" :class="{'required': selfContent.required}">Où en est ce client ?</span>
    <div class="stepper-track" role="radiogroup" :aria-label="selfContent.label" :class="{'missing': isMissing}">
      <template v-for="(step, index) in steps" :key="step.value">
        <span v-if="index > 0" class="step-line" :class="{'is-done': index <= currentIndex}" aria-hidden="true"></span>
        <button type="button" role="radio" class="step" :class="stepState(index)" :aria-checked="index === currentIndex" :disabled="selfContent.readOnly" @click="select(step.value)">
          <span class="step-node">
            <svg v-if="index < currentIndex" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
            <template v-else>{{ index + 1 }}</template>
          </span>
          <span class="step-label">{{ step.label }}</span>
        </button>
      </template>
      <!-- « Non intéressé » est une sortie du parcours, pas une étape : nœud à part, relié en pointillés -->
      <span class="exit-line" aria-hidden="true"></span>
      <button type="button" role="radio" class="step exit-step" :class="{'is-current': isUninterested}" :aria-checked="isUninterested" :disabled="selfContent.readOnly" @click="select(uninterested.value)">
        <span class="step-node">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
        </span>
        <span class="step-label">{{ uninterested.label }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import {CLIENT_STATUS_ENUM, CLIENT_STATUS_ENUM_ARRAY} from "@/models/clientStatusEnum";

// Parcours commercial : les statuts dans l'ordre, hors « Non intéressé » qui sort du parcours
const STEPS = CLIENT_STATUS_ENUM_ARRAY.filter(status => status.value !== CLIENT_STATUS_ENUM.UNINTERESTED.value);

export default {
  name: 'ClientStatusStepper',
  props: ['content'],
  data() {
    return {
      selfContent: this.content,
      steps: STEPS,
      uninterested: CLIENT_STATUS_ENUM.UNINTERESTED
    };
  },
  computed: {
    // Position du client dans le parcours (-1 : aucun statut ou « Non intéressé »)
    currentIndex() {
      return this.steps.findIndex(step => step.value === this.selfContent.value);
    },
    isUninterested() {
      return this.selfContent.value === this.uninterested.value;
    },
    // Champ obligatoire encore vide
    isMissing() {
      const value = this.selfContent.value;
      return !!this.selfContent.required && !this.selfContent.readOnly && (value === '' || value === undefined || value === null);
    }
  },
  methods: {
    stepState(index) {
      return {'is-done': index < this.currentIndex, 'is-current': index === this.currentIndex};
    },
    select(value) {
      if (this.selfContent.readOnly) { return }
      this.selfContent.error = false;
      this.selfContent.value = value;
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.stepper-label
  display block
  margin 0 0 6px 2px
  font-size 0.82em
  font-weight 600
  color CTRL_LABEL
  &.required::after
    content " *"
    color CTRL_ERROR

.stepper-track
  display flex
  align-items flex-start
  min-width 0
  border-radius CTRL_RADIUS
  &.missing
    box-shadow 0 0 7px CTRL_MISSING_GLOW

.step
  display flex
  flex-direction column
  align-items center
  gap 4px
  flex 0 0 auto
  width 108px
  padding 0
  font inherit
  color CTRL_LABEL
  background transparent
  border none
  cursor pointer
  &:disabled
    cursor auto
  &:focus-visible
    outline none
    .step-node
      box-shadow 0 0 0 2px white, 0 0 0 4px CTRL_FOCUS

.step-node
  display flex
  align-items center
  justify-content center
  width 28px
  height 28px
  box-sizing border-box
  border-radius 50%
  font-size 0.9em
  font-weight 800
  color CTRL_LABEL
  background-color white
  border 2px solid #cfd8e8
  transition background-color CTRL_EASE, border-color CTRL_EASE

.step:not(:disabled):hover .step-node
  border-color THEME_COLOR_PRIMARY_FADDED

.step-label
  font-size 0.86em
  font-weight 600
  line-height 1.2
  text-align center

// Étapes passées : bleu plein et coche
.step.is-done .step-node
  color THEME_COLOR_PRIMARY_OPPOSITE
  background-color THEME_COLOR_PRIMARY
  border-color THEME_COLOR_PRIMARY

// Étape en cours : citron (le jaune indique la position dans le processus)
.step.is-current
  color FONT_COLOR
  .step-node
    color FONT_COLOR
    background-color PAGE_LEMON_HOVER
    border-color PAGE_LEMON
  .step-label
    font-weight 800

.step-line
  flex 1 1 0
  min-width 12px
  height 2px
  margin-top 15px
  border-radius 2px
  background-color #cfd8e8
  &.is-done
    background-color THEME_COLOR_PRIMARY

// Sortie du parcours : trait pointillé et nœud rouge pâle (plein quand « Non intéressé » est choisi)
.exit-line
  flex 0 0 auto
  width 22px
  height 0
  margin-top 15px
  border-top 2px dashed #e3c3cb

.exit-step
  .step-node
    color CTRL_ERROR
    background-color white
    border 2px dashed #e3c3cb
  &:not(:disabled):hover .step-node
    border-color CTRL_ERROR
  .step-label
    font-weight 600
  &.is-current
    color CTRL_ERROR
    .step-node
      color CTRL_ERROR
      background-color CTRL_ERROR_BG
      border 2px solid CTRL_ERROR
    .step-label
      color CTRL_ERROR
      font-weight 800

@media only screen and (max-width: TABLET_SIZE)
  .step
    flex 1 1 0
    width auto
    min-width 0
  .step-line
    min-width 4px
    flex-basis 8px
    flex-grow 0
  .exit-line
    width 10px
</style>
